import { esWeb } from './entorno';
import { NOMBRE_FLUJO, NOMBRE_USO, normalizarPantalla, type EventoUso } from '@shared/usoEventos';
import { nuevaClaveIntento } from './claveIntento';

/**
 * Sprint 161 — El lado del navegador del monitoreo de uso.
 *
 * Junta eventos en una cola y los manda en lotes (cada 20 s, al juntar 30, o cuando la pestaña se
 * oculta). Nada de esto puede estorbar la venta: no espera respuesta, no reintenta en bucle y, si el
 * servidor no contesta, los eventos se tiran. Sólo en el track web: el escritorio no tiene a dónde
 * mandarlos.
 *
 * Lo que se registra lo decide el esquema compartido (`usoEventos.ts`): rutas y nombres marcados con
 * `data-uso`. El texto de los botones NO se manda: en una pantalla de pedidos, un botón puede llevar
 * el nombre o el teléfono de un cliente.
 */

// Sin generador de claves no hay monitoreo: una visita que no es UUID haría rechazar cada lote.
const visita = nuevaClaveIntento();
let cola: EventoUso[] = [];
let activo = false;
let pantallaActual: { ruta: string; desde: number } | null = null;

function encolar(e: Omit<EventoUso, 'en'>): void {
  if (!activo) return;
  cola.push({ ...e, en: Date.now() });
  if (cola.length >= 30) void enviar();
}

export async function enviar(alSalir = false): Promise<void> {
  if (cola.length === 0) return;
  if (!visita) return;
  const lote = { visita, eventos: cola.slice(0, 100) };
  cola = cola.slice(100);
  try {
    await fetch('/api/uso', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: alSalir,
      body: JSON.stringify(lote),
    });
  } catch {
    /* medir es secundario: si no llega, se pierde */
  }
}

/** Se enciende al entrar con sesión y se apaga al salir. */
export function activarMonitoreo(encendido: boolean): void {
  const antes = activo;
  activo = encendido && esWeb() && Boolean(visita);
  if (antes && !activo) {
    cerrarPantalla();
    void enviar(true);
    pantallaActual = null;
  }
}

function cerrarPantalla(): void {
  if (!pantallaActual) return;
  // Una pantalla de cocina puede quedar abierta días: se acota al máximo que acepta el servidor.
  const ms = Math.min(86_400_000, Math.max(0, Date.now() - pantallaActual.desde));
  encolar({ tipo: 'pantalla', pantalla: pantallaActual.ruta, duracionMs: ms });
}

/** Cambio de pantalla: se registra la anterior con el tiempo que se estuvo en ella. */
export function verPantalla(ruta: string): void {
  if (!activo) return;
  const r = normalizarPantalla(ruta);
  if (pantallaActual?.ruta === r || r === '/login') return; // la entrada no es un hábito: ruido
  cerrarPantalla();
  pantallaActual = { ruta: r, desde: Date.now() };
}

/** Un toque. `nombre` sólo si el elemento lo declaró con `data-uso`. */
export function registrarToque(nombre?: string): void {
  const limpio = nombre && NOMBRE_USO.test(nombre) ? nombre : undefined;
  encolar({ tipo: 'toque', pantalla: pantallaActual?.ruta ?? '/', nombre: limpio });
}

/**
 * Un flujo que empieza y termina (vender, cerrar la caja). `clave` une el inicio con su fin; un
 * inicio sin fin es lo que se abandonó a medias.
 */
export function marcarFlujo(flujo: string, etapa: 'inicio' | 'fin', clave: string): void {
  const nombre = `${flujo}:${etapa}:${clave.toLowerCase()}`;
  if (!NOMBRE_FLUJO.test(nombre)) return;
  encolar({ tipo: 'flujo', pantalla: pantallaActual?.ruta ?? '/', nombre });
}

let instalado = false;
/** Escucha los toques de toda la app (una sola vez) y vacía la cola periódicamente. */
export function instalarMonitoreo(): void {
  if (instalado || !esWeb()) return;
  instalado = true;
  document.addEventListener(
    'click',
    (ev) => {
      const el = ev.target instanceof Element ? ev.target : null;
      if (!el) return;
      const marcado = el.closest('[data-uso]');
      if (marcado) return registrarToque(marcado.getAttribute('data-uso') ?? undefined);
      if (el.closest('button, a, [role="button"]')) registrarToque();
    },
    true,
  );
  window.setInterval(() => void enviar(), 20_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      cerrarPantalla();
      void enviar(true);
      if (pantallaActual) pantallaActual = { ruta: pantallaActual.ruta, desde: Date.now() };
    }
  });
}
